import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { BUCKET_FUNCTIONS_REGION, FUNCTIONS_REGION } from './functionsRegion';
import { getMessaging, isSupported } from 'firebase/messaging';
import { emulatorConfig } from './emulatorMode';

const productionConfig = {
  apiKey: "AIzaSyBaPV4YzG6IfzsNN-iuLHBPhYF2yakSWkE",
  authDomain: "mt-app-backend.firebaseapp.com",
  projectId: "mt-app-backend",
  storageBucket: "mt-app-backend.firebasestorage.app",
  messagingSenderId: "615386169185",
  appId: "1:615386169185:web:68d2ed0dd2d51a0370cf20",
  measurementId: "G-E4S339E7SN"
};

// Local emulator mode (perf suite only): needs a REACT_APP_USE_EMULATORS
// build AND a localhost page - see emulatorMode.ts. Null in production.
const emulators = emulatorConfig();

const firebaseConfig = emulators
  ? { ...productionConfig, projectId: emulators.projectId, storageBucket: `${emulators.projectId}.appspot.com`, authDomain: `${emulators.projectId}.firebaseapp.com` }
  : productionConfig;

const app = initializeApp(firebaseConfig);

// App Check: attest that requests come from the real web app (reCAPTCHA v3).
// Gated on the site key so localhost and CI run unverified until the key is
// created in the App Check console - enforcement is off there, so unverified
// only means it shows in the metrics. Must run before the first
// Firestore/Functions/Storage call or those requests go out tokenless.
// In development the debug flag makes the SDK mint a debug token and log it
// to the browser console; register it under App Check -> Apps -> Manage
// debug tokens instead of letting reCAPTCHA score localhost traffic.
const appCheckSiteKey = process.env.REACT_APP_APPCHECK_RECAPTCHA_SITE_KEY;
if (appCheckSiteKey && !emulators) {
  if (process.env.NODE_ENV === 'development') {
    (window as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean } & Window).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);

// Initialize Firestore - auto-detect tries WebSocket first, falls back to long polling if blocked
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
});

export const storage = getStorage(app);

// Passing the region explicitly matters: a bare getFunctions(app) silently
// pins the SDK default (us-central1), which stops matching the deployment
// the moment the functions move region.
export const functions = getFunctions(app, FUNCTIONS_REGION);

if (emulators) {
  const { host } = emulators;
  connectAuthEmulator(auth, `http://${host}:${emulators.authPort}`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, emulators.firestorePort);
  connectStorageEmulator(storage, host, emulators.storagePort);
  // getFunctions caches one instance per region, so connecting these two
  // also covers the callers that fetch their own (ResourcesDataHandlers).
  connectFunctionsEmulator(functions, host, emulators.functionsPort);
  connectFunctionsEmulator(getFunctions(app, BUCKET_FUNCTIONS_REGION), host, emulators.functionsPort);
}

// Initialize messaging with support check for web push notifications
export const messaging = (async () => {
  // Web push talks to Google directly; never in emulator mode.
  if (emulators) return null;
  try {
    const supported = await isSupported();
    if (supported) {
      return getMessaging(app);
    }
    console.warn('FCM is not supported in this browser');
    return null;
  } catch (error) {
    console.warn('Error initializing FCM:', error);
    return null;
  }
})();

// Performance Monitoring is deliberately NOT initialised here - see
// ./performance.ts. It is loaded dynamically from index.tsx so that the SDK
// (and its ESM-only web-vitals dependency) stays out of both the entry bundle
// and every Jest suite that transitively imports this module.


export default app;